import React, { useEffect } from 'react'
import ExpandedNavItemProvider from './ExpandedNavItemProvider.tsx'
import ExpandedSideNavProvider from './ExpandedSideNavProvider.tsx'
import FlashMessageProvider from '../ui/FlashMessageProvider.tsx';
import NavigationLayout from './NavigationLayout.tsx'
import {
  currentUserContext,
  localeContext,
  appCopyContext,
  hubAppCopyContext,
} from '../../contexts'
import { User, AppCopy } from '../../types'
import { Helmet } from 'react-helmet-async'
import { toISOCode } from '../../utils'
import {
  buildProgramUnitIndex,
  resolveContentfulResponse,
  buildContentModel,
} from '../../utils/contentfulUtils'
import { api } from '../../utils/apiClient'

interface ApplicationLayoutProps {
  children: React.ReactNode
  currentUser: User
  appCopy: AppCopy
  hubAppCopy: AppCopy
  locale: string
  program?: Record<string, unknown> // TODO: Define proper Program interface
  programs: Record<string, unknown>[] // TODO: Define proper Program interface
  userApps: string[]
  masqueradedUser?: boolean
  product?: string
  flashMessage?: string
  userPrograms?: Record<string, unknown>[]
  activeHubSlug?: string
}

// Layout component that wraps all view components.
function ApplicationLayout({
  children,
  currentUser,
  appCopy,
  locale,
  userApps,
  programs,
  userPrograms,
  program,
  hubAppCopy,
  activeHubSlug,
  masqueradedUser = false,
  product,
  flashMessage,
}: ApplicationLayoutProps) {
  // Programs from ProgramsWrapper are already processed, don't run through resolveContentfulResponse  
  const updatedPrograms = programs || []
  const updatedUserPrograms = resolveContentfulResponse(userPrograms || [])

  // Handle program data - check if it's already processed or needs processing
  let updatedProgram = program
  if (program && program.fields) {
    // Program has fields, needs to be processed through buildContentModel
    updatedProgram = buildContentModel(program)
    
    // Add navTitle to level1ProgramUnits for sidebar navigation
    if (updatedProgram?.level1ProgramUnits) {
      updatedProgram.level1ProgramUnits = updatedProgram.level1ProgramUnits.map((unit) => ({
        ...unit,
        navTitle: unit.title_prefix ? `${unit.title_prefix} - ${unit.title}` : unit.title,
      }))
    }
  }
  
  const programUnitIndex = buildProgramUnitIndex(updatedProgram)

  // APP COPY - Check if already resolved or needs resolving
  const updatedAppCopy = appCopy?.sys ? resolveContentfulResponse(appCopy)[0] : appCopy

  // HUB APP COPY - Check if already resolved or needs resolving
  const updatedHubAppCopy = hubAppCopy?.sys ? resolveContentfulResponse(hubAppCopy)[0] : hubAppCopy

  useEffect(() => {
    const handleCopy = (event) => {
      // Bypass copy override for Kaiya chat pages
      if (product === 'change_coach' || window.location.pathname.startsWith('/kaiya')) return
      const selection = document.getSelection()
      event.clipboardData.setData(
        'text/html',
        `${selection}<br /><br />${updatedAppCopy?.attribution || ''}`
      )
      event.preventDefault()
    }
    window.addEventListener('copy', handleCopy)
    return () => window.removeEventListener('copy', handleCopy)
  }, [product, updatedAppCopy?.attribution])

  return (
    <>
      {/* Set global html lang attribute */}
      <Helmet>
        <html lang={toISOCode(locale)} />
      </Helmet>
      {/* Provide locale context to app */}
      {/* Masquerade Banner - Sticky footer warning when admin is impersonating a user */}
      {masqueradedUser && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2147483647, // Max z-index to ensure it's above chat widgets
            backgroundColor: '#f59e0b',
            color: '#1f2937',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
            pointerEvents: 'auto',
          }}
        >
          <span>
            <strong>Masquerading as:</strong> {currentUser.email}
          </span>
          <button
            type="button"
            onClick={async () => {
              const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
              try {
                // Call API to stop masquerading - backend restores the original admin session
                const response = await api.stopMasquerading();
                const redirectPath = response.data?.redirect_url;
                window.location.href = redirectPath
                  ? `${adminUrl}${redirectPath}`
                  : `${adminUrl}/users`;
              } catch (err) {
                console.warn('Stop masquerading API call failed:', err);
                window.location.href = `${adminUrl}/users`;
              }
            }}
            style={{
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              pointerEvents: 'auto',
            }}
          >
            Stop Masquerading
          </button>
        </div>
      )}
      <localeContext.Provider value={locale}>
        {/* Provide hub app copy context to app */}
        <hubAppCopyContext.Provider value={updatedHubAppCopy}>
          {/* Provide app copy context to app */}
          <appCopyContext.Provider value={updatedAppCopy}>
            {/* Provide current user context to app */}
            <currentUserContext.Provider value={currentUser}>
              {/* Provide expanded hub side navigation context to app */}
              <ExpandedSideNavProvider>
                {/* Provide expanded side navigation items context to app */}
                <ExpandedNavItemProvider
                  programName={
                    updatedProgram ? updatedProgram.programName : null
                  }
                >
                  {/* Provide flash message context to app */}
                  <FlashMessageProvider value={flashMessage}>
                    <NavigationLayout
                      userApps={userApps}
                      program={updatedProgram}
                      programs={updatedPrograms}
                      userPrograms={updatedUserPrograms}
                      activeHubSlug={activeHubSlug}
                      product={product}
                      masqueradedUser={masqueradedUser}
                    >
                      {React.Children.map(children, (child) =>
                        React.cloneElement(child, {
                          programs: updatedPrograms,
                          userPrograms: updatedUserPrograms,
                          program: updatedProgram,
                          programUnitIndex: programUnitIndex,
                          activeHubSlug: activeHubSlug,
                          currentUser: currentUser,
                        })
                      )}
                    </NavigationLayout>
                  </FlashMessageProvider>
                </ExpandedNavItemProvider>
              </ExpandedSideNavProvider>
            </currentUserContext.Provider>
          </appCopyContext.Provider>
        </hubAppCopyContext.Provider>
      </localeContext.Provider>
    </>
  )
}

export default ApplicationLayout
